/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {loadDevicesBySmartSearch} from '@ion/core/src/services/Connect';
import {Device} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeviceResults = UseDefaultQueryResult & {
  device: Device;
  refresh: Function;
};

export interface UseSmartSearchDeviceParams {
  id?: number | string;
  esnAssociatedLMU?: string;
  isAssetId?: boolean;
  ignoreFormat?: boolean;
  device?: Device;
  isPulseUrl?: boolean;
  includeSCISettings?: boolean;
  isReturnSingleDevice?: boolean;
};

/**
 * Retrieves the device details for the given device id from a smart search.
 *
 * @param id of the device to retrieve details for
 * @param enabled if the device is enabled / disabled
 * @param staleTime how long the query should hold onto details before re-fetching
 * @returns a query result
 */
export const useSmartSearchDevice = (
  {
    id,
    esnAssociatedLMU,
    ignoreFormat = false,
    isAssetId = false,
    isPulseUrl = true,
    includeSCISettings = false,
    isReturnSingleDevice = true
  }: UseSmartSearchDeviceParams,
  enabled = true,
  staleTime = getStaleTime(1, false, false)
): UseDeviceResults => {
  const queryClient = useQueryClient();
  const query = useQuery([QueryKey.DEVICE_GET, id, 'smartSearch'], async() => {
    const queryString = ignoreFormat ? id : esnAssociatedLMU
      ? `(extensions.esnAssociatedLMU:${esnAssociatedLMU})`
      : isAssetId ? `(asset.id:${id})` : `(id:${id})`;
    if (esnAssociatedLMU || id) {
      const {devices} = await loadDevicesBySmartSearch({
        queryString,
        includeDeviceSettings : includeSCISettings,
        withLocation          : true,
        withIdReport          : true,
        includeGroups         : true,
        includeSubGroups      : true,
        includeAccountFilter  : true,
        hasPendingFileHistory : true,
        usePulse              : isPulseUrl
      });
      return isReturnSingleDevice ? devices?.[0] : devices;
    }
    return {};
  }, {
    staleTime : staleTime as number,
    enabled
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries([QueryKey.DEVICE_GET, id, 'smartSearch']);
  }, [id]);

  return createQueryResult(query, {
    device : query.data || {},
    refresh
  }) as UseDeviceResults;
};

export default useSmartSearchDevice;
